import React, { useContext } from 'react';

import { AppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { FontEmphasis, FontWeight } from '@prisjakt/ui/text';
import { CaptionText } from '@prisjakt/ui/text/variants';

import { OptionTypeEnum } from 'src/components/filter-dropdown';

import {
  getShippingDays,
  getDeliveryTimeText,
  isShippingInstorePickup,
  getLabel,
  ShippingType,
  getDeliveryTextIsBold,
} from './utils';

import type {
  ProductOfferAvailability,
  Shipping,
  StockStatus,
} from 'src/types';

import { StyledDeliveryText, StyledDeliveryTime, StyledLabel } from './styles';
import { StockStatusEnum } from '../stock-price-info/types';
import { ViewSize } from '../../types';

interface IDeliveryTimeProps {
  shipping?: Shipping | null;
  stockStatus?: StockStatus | null;
  availability?: ProductOfferAvailability | null;
}

export const DeliveryTime = (props: IDeliveryTimeProps) => {
  const { shipping, stockStatus, availability } = props;

  const { t, market } = useContext(AppContext);

  const { sorting, viewSize } = usePriceRowContext();

  let sort = ShippingType.CHEAPEST;

  if (sorting === OptionTypeEnum.FASTEST_DELIVERY) {
    sort = ShippingType.FASTEST;
  }

  const label = getLabel(t, stockStatus);

  const isInstorePickup = isShippingInstorePickup(shipping);
  const shippingDays = getShippingDays(t, shipping);
  const deliveryText = getDeliveryTimeText(
    t,
    market,
    shippingDays,
    stockStatus,
    availability
  );
  const highlightText = getDeliveryTextIsBold(
    shippingDays,
    stockStatus,
    availability,
    isInstorePickup
  );

  const deliveryEmphasis = highlightText
    ? FontEmphasis.HIGH
    : FontEmphasis.MEDIUM;

  const deliveryFontWeight = highlightText
    ? FontWeight.HEAVIEST
    : FontWeight.NORMAL;

  const hasAvailabilityDate =
    availability?.availabilityDate &&
    (stockStatus === StockStatusEnum.INCOMING ||
      stockStatus === StockStatusEnum.PREORDER);

  const showDeliveryTime =
    shippingDays ||
    hasAvailabilityDate ||
    isInstorePickup ||
    sort === ShippingType.FASTEST;

  const renderMobileDeliveryTime = () => {
    return (
      <div>
        {!isInstorePickup && (
          <StyledLabel emphasis={FontEmphasis.MEDIUM}>{label}</StyledLabel>
        )}
        <CaptionText emphasis={deliveryEmphasis} weight={deliveryFontWeight}>
          {isInstorePickup
            ? t('product', `delivery-option.pickup-instore`)
            : deliveryText}
        </CaptionText>
      </div>
    );
  };

  const renderDesktopDeliveryTime = () => {
    return (
      <StyledDeliveryText>
        {!isInstorePickup && (
          <CaptionText emphasis={FontEmphasis.MEDIUM}>{label}</CaptionText>
        )}
        <CaptionText emphasis={deliveryEmphasis} weight={deliveryFontWeight}>
          {isInstorePickup
            ? t('product', `delivery-option.pickup-instore`)
            : deliveryText}
        </CaptionText>
      </StyledDeliveryText>
    );
  };

  // If no delivery time is available,
  // no delivery text should be visible unless sorting on fastest delivery.
  if (!showDeliveryTime) {
    return null;
  }

  return (
    <StyledDeliveryTime>
      {viewSize === ViewSize.SMALL && renderMobileDeliveryTime()}
      {viewSize >= ViewSize.MEDIUM && renderDesktopDeliveryTime()}
    </StyledDeliveryTime>
  );
};
